import React, { useMemo, useRef, useState } from 'react';

import {
  HStack, Grid, Stack, Text, Flex, StackProps, Input,
} from '@chakra-ui/react';
import { useMutation } from 'react-query';

import { uploadFile } from 'api/s3';
import { updateAvatarUrl } from 'api/users';
import { Button, Icon, StatusBadge } from 'components';
import Avatar from 'components/avatar/default';
import ZoomImage from 'containers/Modals/ZoomImage';
import { UserTypes } from 'enums/types';

function InfoRow({
  label, value, color, variant,
}) {
  return (
    <Stack spacing={1}>
      <Text variant="smallSemiBold">{label}</Text>
      <Text
        variant={variant ?? 'smallRegular'}
        color={color ?? 'gray.600'}>{value || 'N/A'}
      </Text>
    </Stack>
  );
}

function UpdateAvatar({ user }) {
  const ref = useRef(null);

  const {
    mutate,
    isLoading,
  } = useMutation(async ({ url }: any) => updateAvatarUrl({
    id: user?.id, avatarUrl: url,
  }),
  {
    onSuccess: () => window.location.reload(),
  });

  const {
    mutate: mutateS3,
    isLoading: isLoadingS3,
  } = useMutation((file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return uploadFile(formData);
  }, {
    onSuccess: ({ data }) => mutate(data),
  });

  return (
    <>
      <Input
        type="file"
        multiple={false}
        ref={ref}
        onChange={(event) => mutateS3(event.target.files[0])}
        hidden
        name="avatarUrl"
      />
      <Button
        mt={2}
        size="sm"
        isLoading={isLoadingS3 || isLoading}
        onClick={() => ref?.current?.click()}
        variant="secondary.teal">
        <Icon
          size="xs"
          mr={1}
          name="camera"
          family="far"
        />
        <Text variant="xSmallBold">
          Editar avatar
        </Text>
      </Button>
      <Button
        size="sm"
        isLoading={isLoadingS3 || isLoading}
        onClick={() => mutate({ url: null })}
        variant="ghost.teal">
        <Text
          color="system.error.default"
          variant="xSmallRegular">
          Remover avatar
        </Text>
      </Button>
    </>
  );
}

function AvatarField({ user }) {
  const [image, setImage] = useState<string>();
  const isProfile = useMemo(() => window.location.pathname.includes('profile'), []);
  return (
    <Flex
      alignItems="center"
      minWidth={130}
      flexDir="column"
      mr={10}>
      <ZoomImage
        isOpen={!!image}
        onClose={() => setImage(undefined)}
        data={image}
      />
      <Avatar
        onClick={() => setImage(user.avatarUrl)}
        h={100}
        w={100}
        name={user.name}
        src={user.avatarUrl}
      />
      {isProfile && <UpdateAvatar user={user} />}
    </Flex>
  );
}

interface UserInfoProps extends StackProps {
  user: any
}

export default function UserInfo({ user, ...rest }:UserInfoProps) {
  const fields = [
    {
      label: 'Tipo de permissão:',
      value: user.role?.name,
      color: 'primary.default',
      variant: 'smallSemiBold',
    },
    {
      label: user.type === UserTypes.Driver ? 'CNH:' : 'CPF:',
      value: user.documentNumber,
    },
    {
      label: 'Telefone:',
      value: user.phone,
    },
    {
      label: 'Empresa:',
      value: user.company?.name,
    },
    {
      label: 'Email:',
      value: user.email,
    },
    {
      label: 'Regional:',
      value: user.regional?.name,
    },
  ];

  return (
    <HStack
      maxW="1024px"
      bg="white.default"
      borderRadius={8}
      p={6}
      {...rest}>
      <AvatarField user={user} />
      <Grid
        width="100%"
        gap={6}
        templateColumns="repeat(2,1fr)">
        {fields.map(InfoRow)}
      </Grid>
      <Flex h="100%">
        <Flex h="min">
          <StatusBadge status={user?.status} />
        </Flex>
      </Flex>
    </HStack>
  );
}
